import i18n, { titleCase } from '@rygen/rygen-ui/i18n';
import {
  CapacityProviderType,
  CarrierRef,
  Currency,
  EquipmentType,
  Mode,
  TenderConfig,
} from '@rygen/rygen-ui/model';

import { Carrier } from '../Carrier';
import { ThirdPartyRateProvider } from '../ThirdPartyRateProvider';

import { BrokerRateProviderDetails } from './BrokerRateProviderDetails';
import { DATRateProviderDetails } from './DATRateProviderDetails';
import { LaneQuoteData } from './LaneQuoteData';
import { P44RateProviderDetails } from './P44RateProviderDetails';
import { RateProviderDetails } from './RateProviderDetails';
import { RateQuoteAccessorial } from './RateQuoteAccessorial';
import { RateQuoteCharge } from './RateQuoteCharge';
import { RateUplift } from './RateUplift';
import { RateUsage } from './RateUsage';
import { RoutingGuideClassification } from './RoutingGuideClassification';
import { WellKnownRateStrategy } from './WellKnownRateStrategy';
import { WellKnownService } from './WellKnownService';

export class RateQuote {
  id?: number;
  contractId?: number;
  spotMarketId?: number;
  contractName?: string;
  rateStatus?: string;
  message?: string;
  source?:
    'CONTRACT' | 'BROKERAGE' | 'SPOT_QUOTE' | 'LANE_QUOTE' | 'SPOT_MARKET' =
    'CONTRACT';
  rateStrategyType?: string;
  rateUsage?: RateUsage;
  auditOnly?: boolean;
  originalDATRate?: boolean;
  carrier?: Carrier;
  brokeredCarrier?: CarrierRef;
  mode?: Mode;
  transitTime?: number;
  deliveryWindowStart?: string;
  deliveryWindowEnd?: string;
  totalMileage?: number;
  equipmentType?: EquipmentType;
  service?: string;
  serviceMethod?: string;
  serviceType?: string;
  serviceTypeDescription?: string;
  quoteExpiration?: Date;
  carrierQuoteNumber?: string;
  currencyCode?: Currency = Currency.USD;
  rawTotalPrice?: number;
  totalPrice?: number;
  discountAmount?: number;
  rawLineHaulNetCharge?: number;
  rawLineHaulGrossCharge?: number;
  lineHaulNetCharge?: number;
  lineHaulGross?: number;
  upliftAmount?: number;
  upliftRule?: RateUplift;
  fuelSurchargeAmount?: number;
  upliftedFuelAmount?: number;
  fuelUpliftAmount?: number;
  fuelUpliftRule?: RateUplift;
  accessorials: RateQuoteAccessorial[] = [];
  actualWeight?: number;
  billedWeight?: number;
  ratedDate?: Date;
  createdBy?: string;
  warnings: string[] = [];
  requestedDeliveryDate?: Date;
  brokerageTenderConfigOverride?: TenderConfig;
  laneQuoteData?: LaneQuoteData;
  routingGuideClassification?: RoutingGuideClassification;
  rateProviderDetails?: RateProviderDetails;
  doNotDispatch: boolean = false;
  carrierComplianceRuleWarnings: string[] = [];
  thirdPartyRateProvider?: ThirdPartyRateProvider;
  billToOverrideId?: number;

  get serviceTypeForFiltering(): string {
    const normalizedType = titleCase(this.serviceType);
    if (normalizedType === 'Std') {
      return WellKnownService.STANDARD;
    } else if (this.rateStrategyType === WellKnownRateStrategy.P44_VOLUME) {
      return `Volume (${normalizedType})`;
    } else {
      return normalizedType ?? WellKnownService.UNKNOWN;
    }
  }

  get rawCharges(): RateQuoteCharge[] {
    const charges: RateQuoteCharge[] = [];

    charges.push({
      charge: (this.rawLineHaulNetCharge || 0) + (this.discountAmount || 0),
      code: 'LINEHAUL',
      description: i18n.global.t('shipments.financials.linehaul'),
    });

    if (this.discountAmount || this.mode === Mode.LTL) {
      charges.push({
        charge: (this.discountAmount ?? 0) * -1,
        code: 'DISCOUNT',
        description: i18n.global.t('shipments.financials.discounts'),
      });
    }

    charges.push({
      charge: this.fuelSurchargeAmount ?? 0,
      code: 'FUEL',
      description: i18n.global.t('shipments.financials.fuel'),
    });

    charges.push({
      charge: this.upliftAmount ?? 0,
      code: 'UPLIFT',
      description: i18n.global.t('rateShop.uplift'),
    });

    for (const acc of this.accessorials || []) {
      charges.push({
        charge: acc.charge || 0,
        code: acc.code?.naturalId || '-',
        description: acc.accessorialDescription,
        stopSequenceNumber: acc.stopSequenceNumber,
      });
    }

    return charges;
  }

  get charges(): RateQuoteCharge[] {
    const charges: RateQuoteCharge[] = this.rawCharges.filter(
      (c) => c.code !== 'UPLIFT',
    );

    const linehaulIndex = charges.findIndex((c) => c.code === 'LINEHAUL');
    const linehaulCharge = {
      ...charges[linehaulIndex],
      charge: (this.lineHaulNetCharge || 0) + (this.discountAmount || 0),
    };
    charges.splice(linehaulIndex, 1, linehaulCharge);

    return charges;
  }

  get rawChargesRedesign(): RateQuoteCharge[] {
    const charges: RateQuoteCharge[] = [];

    if (this.discountAmount || this.mode === Mode.LTL) {
      charges.push({
        code: 'LINEHAUL_GROSS',
        charge: this.rawLineHaulGrossCharge ?? 0,
      });

      charges.push({
        code: 'LINEHAUL_DISCOUNT',
        charge: (this.discountAmount ?? 0) * -1,
      });
    }

    charges.push({
      code: 'LINEHAUL_NET',
      description: i18n.global.t('shipments.financials.linehaul'),
      charge: this.rawLineHaulNetCharge ?? 0,
      margin: this.upliftAmount ?? 0,
      sell: this.lineHaulNetCharge ?? 0,
    });

    charges.push({
      code: 'FUEL',
      charge: this.fuelSurchargeAmount ?? 0,
      margin: this.fuelUpliftAmount ?? 0,
      sell: this.upliftedFuelAmount ?? 0,
    });

    for (const acc of this.accessorials ?? []) {
      charges.push({
        code: acc.code?.naturalId ?? '-',
        description: acc.accessorialDescription,
        charge: acc.charge ?? 0,
        margin: acc.upliftAmount ?? 0,
        sell: acc.upliftedAmount ?? 0,
        stopSequenceNumber: acc.stopSequenceNumber,
      });
    }

    return charges;
  }

  get chargesRedesign(): RateQuoteCharge[] {
    const charges: RateQuoteCharge[] = [];

    charges.push({
      code: 'LINEHAUL',
      charge: (this.lineHaulNetCharge ?? 0) + (this.discountAmount ?? 0),
    });

    if (this.discountAmount || this.mode === Mode.LTL) {
      charges.push({
        code: 'DISCOUNT',
        charge: (this.discountAmount ?? 0) * -1,
      });
    }

    charges.push({
      code: 'FUEL',
      charge: this.upliftedFuelAmount ?? 0,
    });

    for (const acc of this.accessorials ?? []) {
      charges.push({
        code: acc.code?.naturalId ?? '-',
        description: acc.accessorialDescription,
        charge: acc.upliftedAmount ?? 0,
        stopSequenceNumber: acc.stopSequenceNumber,
      });
    }

    return charges;
  }

  get laneType(): string | undefined {
    return this.service;
  }

  get isCapacityProvider(): boolean {
    const rateStrategyType = this.rateStrategyType ?? '';
    return [
      WellKnownRateStrategy.P44,
      WellKnownRateStrategy.P44_VOLUME,
      WellKnownRateStrategy.PRIORITY1,
    ].includes(rateStrategyType as WellKnownRateStrategy);
  }

  get hasAnyUplift(): boolean {
    return (
      !!(this.upliftRule || this.upliftAmount) ||
      !!(this.fuelUpliftRule || this.fuelUpliftAmount) ||
      this.accessorials.some((a) => !!(a.upliftRule || a.upliftAmount))
    );
  }

  constructor(opts?: Partial<RateQuote>) {
    if (opts?.id != null) {
      this.id = opts.id;
    }
    if (opts?.contractId != null) {
      this.contractId = opts.contractId;
    }
    if (opts?.spotMarketId != null) {
      this.spotMarketId = opts.spotMarketId;
    }
    if (opts?.contractName != null) {
      this.contractName = opts.contractName;
    }
    if (opts?.rateStatus != null) {
      this.rateStatus = opts.rateStatus;
    }
    if (opts?.message != null) {
      this.message = opts.message;
    }
    if (opts?.source != null) {
      this.source = opts.source;
    }
    if (opts?.rateStrategyType != null) {
      this.rateStrategyType = opts.rateStrategyType;
    }
    if (typeof opts?.rateUsage === 'string') {
      this.rateUsage = RateUsage[opts.rateUsage];
    } else if (opts?.rateUsage != null) {
      this.rateUsage = opts.rateUsage;
    }
    if (opts?.auditOnly != null) {
      this.auditOnly = opts.auditOnly;
    }
    if (opts?.originalDATRate != null) {
      this.originalDATRate = opts.originalDATRate;
    }
    if (opts?.carrier != null) {
      this.carrier = new Carrier(opts.carrier);
    }
    if (opts?.brokeredCarrier != null) {
      this.brokeredCarrier = new CarrierRef(opts.brokeredCarrier);
    }
    if (opts?.mode != null) {
      this.mode = opts.mode;
    }
    if (opts?.transitTime != null) {
      this.transitTime = opts.transitTime;
    }
    if (opts?.deliveryWindowStart != null) {
      this.deliveryWindowStart = opts.deliveryWindowStart;
    }
    if (opts?.deliveryWindowEnd != null) {
      this.deliveryWindowEnd = opts.deliveryWindowEnd;
    }
    if (opts?.totalMileage != null) {
      this.totalMileage = opts.totalMileage;
    }
    if (opts?.equipmentType != null) {
      this.equipmentType = opts.equipmentType;
    }
    if (opts?.service != null) {
      this.service = opts.service;
    }
    if (opts?.serviceMethod != null) {
      this.serviceMethod = opts.serviceMethod;
    }
    if (opts?.serviceType != null) {
      this.serviceType = opts.serviceType;
    }
    if (opts?.serviceTypeDescription != null) {
      this.serviceTypeDescription = opts.serviceTypeDescription;
    }
    if (opts?.quoteExpiration != null) {
      this.quoteExpiration = new Date(opts.quoteExpiration);
    }
    if (opts?.carrierQuoteNumber != null) {
      this.carrierQuoteNumber = opts.carrierQuoteNumber;
    }
    if (opts?.currencyCode != null) {
      this.currencyCode = opts.currencyCode;
    }
    if (opts?.rawTotalPrice != null) {
      this.rawTotalPrice = opts.rawTotalPrice;
    }
    if (opts?.totalPrice != null) {
      this.totalPrice = opts.totalPrice;
    }
    if (opts?.discountAmount != null) {
      this.discountAmount = opts.discountAmount;
    }
    if (opts?.rawLineHaulNetCharge != null) {
      this.rawLineHaulNetCharge = opts.rawLineHaulNetCharge;
    }
    if (opts?.rawLineHaulGrossCharge != null) {
      this.rawLineHaulGrossCharge = opts.rawLineHaulGrossCharge;
    }
    if (opts?.lineHaulNetCharge != null) {
      this.lineHaulNetCharge = opts.lineHaulNetCharge;
    }
    if (opts?.lineHaulGross != null) {
      this.lineHaulGross = opts.lineHaulGross;
    }
    if (opts?.upliftAmount != null) {
      this.upliftAmount = opts.upliftAmount;
    }
    if (opts?.upliftRule != null) {
      this.upliftRule = new RateUplift(opts.upliftRule);
    }
    if (opts?.fuelSurchargeAmount != null) {
      this.fuelSurchargeAmount = opts.fuelSurchargeAmount;
    }
    if (opts?.upliftedFuelAmount != null) {
      this.upliftedFuelAmount = opts.upliftedFuelAmount;
    }
    if (opts?.fuelUpliftAmount != null) {
      this.fuelUpliftAmount = opts.fuelUpliftAmount;
    }
    if (opts?.fuelUpliftRule != null) {
      this.fuelUpliftRule = new RateUplift(opts.fuelUpliftRule);
    }
    if (opts?.accessorials != null) {
      this.accessorials = [
        ...opts.accessorials.map((x) => new RateQuoteAccessorial(x)),
      ];
    }
    if (opts?.actualWeight != null) {
      this.actualWeight = opts.actualWeight;
    }
    if (opts?.billedWeight != null) {
      this.billedWeight = opts.billedWeight;
    }
    if (opts?.ratedDate != null) {
      this.ratedDate = new Date(opts.ratedDate);
    }
    if (opts?.createdBy != null) {
      this.createdBy = opts.createdBy;
    }
    if (opts?.warnings != null) {
      this.warnings = opts.warnings;
    }
    if (opts?.requestedDeliveryDate != null) {
      this.requestedDeliveryDate = new Date(opts.requestedDeliveryDate);
    }
    if (opts?.brokerageTenderConfigOverride != null) {
      this.brokerageTenderConfigOverride = new TenderConfig(
        opts.brokerageTenderConfigOverride,
      );
    }
    if (opts?.laneQuoteData != null) {
      this.laneQuoteData = new LaneQuoteData(opts.laneQuoteData);
    }
    if (typeof opts?.routingGuideClassification === 'string') {
      this.routingGuideClassification =
        RoutingGuideClassification[opts.routingGuideClassification];
    } else if (opts?.routingGuideClassification != null) {
      this.routingGuideClassification = opts.routingGuideClassification;
    }
    if (opts?.rateProviderDetails != null) {
      if (this.rateStrategyType === 'DAT_BENCHMARK') {
        this.rateProviderDetails = new DATRateProviderDetails(
          opts.rateProviderDetails,
        );
      } else if (this.brokeredCarrier?.type === CapacityProviderType.CARRIER) {
        this.rateProviderDetails = new BrokerRateProviderDetails(
          opts.rateProviderDetails,
        );
      } else {
        this.rateProviderDetails = new P44RateProviderDetails(
          opts.rateProviderDetails,
        );
      }
    }
    if (opts?.doNotDispatch != null) {
      this.doNotDispatch = opts.doNotDispatch;
    }
    if (opts?.carrierComplianceRuleWarnings != null) {
      this.carrierComplianceRuleWarnings = [
        ...opts.carrierComplianceRuleWarnings,
      ];
    }
    if (opts?.thirdPartyRateProvider != null) {
      this.thirdPartyRateProvider = new ThirdPartyRateProvider(
        opts.thirdPartyRateProvider,
      );
    }
    if (opts?.billToOverrideId != null) {
      this.billToOverrideId = opts.billToOverrideId;
    }
  }
}
